import { isMongoObjectId } from "utils/signageStudioBatchRestore";

/**
 * Installation flow: read `installation_order` snapshot from GET /customization-batches/:id.
 * API may attach it at batch root or on a context entry.
 */
export function getInstallationOrderFromBatch(batch: unknown): Record<string, unknown> | null {
  const b = batch as Record<string, unknown>;
  const root = b.installation_order;
  if (root && typeof root === "object") return root as Record<string, unknown>;
  const ctxs = Array.isArray(b.contexts) ? (b.contexts as Array<{ installation_order?: unknown }>) : [];
  for (const c of ctxs) {
    const io = c?.installation_order;
    if (io && typeof io === "object") return io as Record<string, unknown>;
  }
  return null;
}

/** ISO datetime → date input yyyy-mm-dd and time HH:mm (local). */
export function parseIsoToDateAndTime(iso: string | undefined): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date, time: `${hh}:${mm}` };
}

/** Split "First Last" for form fields. */
export function splitFullName(name: string | undefined): { first: string; last: string } {
  const t = String(name ?? "").trim();
  if (!t) return { first: "", last: "" };
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/** Contact fields for SearchBrokerageAgentTeam.restoreSyncContact from installation_order snapshot. */
export function contactFromInstallationOrderSnapshot(io: Record<string, unknown>): {
  fName: string;
  lName: string;
  email: string;
  phone: string;
} {
  const name = String(io.user_name ?? io.agent_name ?? "").trim();
  const parts = name.split(/\s+/).filter(Boolean);
  return {
    fName: parts[0] ?? "",
    lName: parts.slice(1).join(" ") ?? "",
    email: String(io.user_email ?? "").trim(),
    phone: String(io.user_phone ?? "").trim() || "0000000000",
  };
}

/** True for missing or all-zero Mongo ids (snapshot often omits real ids on installation_address). */
function isUsableAddressDocId(id: string | undefined): boolean {
  const s = String(id ?? "").trim();
  return isMongoObjectId(s) && !/^0{24}$/.test(s);
}

/**
 * Display string for Installation Address field (matches AddressAutocomplete onSelect:
 * `addressLine1, city, state, zip`).
 */
export function installationAddressDisplayFromSnapshot(addr: Record<string, any> | null | undefined): string {
  if (!addr || typeof addr !== "object") return "";
  const d = addr.address_details && typeof addr.address_details === "object" ? addr.address_details : addr;
  const line1 = String(d?.addressLine1 ?? "").trim();
  const city = String(d?.city ?? "").trim();
  const state = String(d?.state ?? "").trim();
  const zip = String(d?.zip ?? "").trim();
  if (line1 && city && state && zip) {
    return `${line1}, ${city}, ${state}, ${zip}`;
  }
  return String(addr.label ?? d?.label ?? "").trim();
}

/** Normalize snapshot installation_address → Addresses shape for map + tax (same idea as AddressAutocomplete onSelect). */
export function buildAddressesFromInstallationSnapshot(shipRaw: Record<string, any>): Record<string, any> | null {
  if (!shipRaw || typeof shipRaw !== "object") return null;
  const d =
    shipRaw.address_details && typeof shipRaw.address_details === "object" ? shipRaw.address_details : shipRaw;
  const line1 = String(d?.addressLine1 ?? "").trim();
  const label =
    String(shipRaw.label ?? d?.label ?? "").trim() ||
    line1 ||
    "Installation address";
  const addressDetails = {
    _id: isUsableAddressDocId(d?._id) ? String(d._id) : "",
    label: String(d?.label ?? label),
    addressLine1: line1,
    addressLine2: String(d?.addressLine2 ?? ""),
    city: String(d?.city ?? ""),
    state: String(d?.state ?? ""),
    country: String(d?.country ?? ""),
    zip: String(d?.zip ?? ""),
    cityCode: String(d?.cityCode ?? ""),
    stateCode: String(d?.stateCode ?? ""),
    countryCode: String(d?.countryCode ?? ""),
    latitude: Number(d?.latitude ?? 0) || 0,
    longitude: Number(d?.longitude ?? 0) || 0,
    phone: String(d?.phone ?? ""),
  };
  return {
    _id: isUsableAddressDocId(shipRaw._id) ? String(shipRaw._id) : "",
    user_id: String(shipRaw.user_id ?? ""),
    label,
    first_name: String(shipRaw.first_name ?? ""),
    last_name: String(shipRaw.last_name ?? ""),
    company: String(shipRaw.company ?? ""),
    default_address: !!shipRaw.default_address,
    address_details: addressDetails,
    created_by: shipRaw.created_by,
    updated_by: shipRaw.updated_by,
  };
}

/**
 * When installation snapshot has no usable Mongo id, try to match a saved address by label or street + zip.
 */
export function findInstallationAddressInSavedList(shipRaw: Record<string, any>, addrList: any[]): any | null {
  if (!shipRaw || typeof shipRaw !== "object" || !Array.isArray(addrList) || !addrList.length) return null;
  const det =
    shipRaw.address_details && typeof shipRaw.address_details === "object" ? shipRaw.address_details : shipRaw;
  const line1 = String(det?.addressLine1 ?? "").trim().toLowerCase();
  const zip = String(det?.zip ?? "").trim();
  const snapLabel = String(shipRaw.label ?? det?.label ?? "").trim().toLowerCase();

  for (const a of addrList) {
    if (!a || typeof a !== "object") continue;
    const ad = a.address_details && typeof a.address_details === "object" ? a.address_details : a;
    const aLine = String(ad?.addressLine1 ?? "").trim().toLowerCase();
    const aZip = String(ad?.zip ?? "").trim();
    const aLabel = String(a.label ?? "").trim().toLowerCase();
    if (line1 && zip && aLine === line1 && aZip === zip) return a;
    if (line1 && aLine === line1 && zip && aZip && aZip === zip) return a;
    if (snapLabel && aLabel && (snapLabel === aLabel || snapLabel.includes(aLabel) || aLabel.includes(snapLabel))) {
      return a;
    }
  }
  return null;
}

/**
 * Map batch billing + installation snapshot + loaded address list → form + selected address objects.
 * Mirrors studioRestoreAddressBindings (create order) but uses installation_details.installation_address.
 */
export function studioRestoreInstallationAddressBindings(
  io: Record<string, any>,
  addrList: any[]
): {
  formik: {
    isSameAsBilling: boolean;
    billingAddressId: string;
    installationAddressId: string;
  };
  useOfficeBilling: boolean;
  billingAddressObj: any | null;
  installationAddressObj: any | null;
} {
  const hasOfficeOnOrder = !!(io.office?._id || io.office?.id);
  const agentRaw = io.agent_id;
  const useOfficeBilling = !isMongoObjectId(agentRaw) && hasOfficeOnOrder;

  const bilRaw = io.billing_address;
  const bilId = bilRaw && typeof bilRaw === "object" ? String((bilRaw as { _id?: string })._id ?? "").trim() : "";
  const instDet = io.installation_details as { installation_address?: unknown } | undefined;
  const shipRaw =
    instDet?.installation_address && typeof instDet.installation_address === "object"
      ? (instDet.installation_address as Record<string, any>)
      : null;

  const shipParentId =
    shipRaw && typeof shipRaw === "object" ? String((shipRaw as { _id?: string })._id ?? "").trim() : "";
  const shipDetailsId =
    shipRaw && typeof shipRaw === "object"
      ? String((shipRaw as { address_details?: { _id?: string } }).address_details?._id ?? "").trim()
      : "";

  const shipIdForLookup = isUsableAddressDocId(shipParentId)
    ? shipParentId
    : isUsableAddressDocId(shipDetailsId)
      ? shipDetailsId
      : "";

  const same = !!io.both_address_same;

  const listAddrs = Array.isArray(addrList) && addrList.length ? addrList : [];
  const findAddr = (id: string | undefined) =>
    id && listAddrs.length ? listAddrs.find((a: any) => String(a._id) === String(id)) : null;

  const bilAddr = bilId ? findAddr(bilId) || bilRaw : null;

  let resolvedFromList: any = null;
  if (shipIdForLookup && listAddrs.length) {
    resolvedFromList = findAddr(shipIdForLookup);
  }
  if (!resolvedFromList && !same && shipRaw && listAddrs.length) {
    resolvedFromList = findInstallationAddressInSavedList(shipRaw, listAddrs);
  }

  let instAddr: any = null;
  if (same && bilId) {
    instAddr = bilAddr;
  } else if (resolvedFromList) {
    instAddr = resolvedFromList;
  } else if (!same && shipRaw) {
    instAddr = buildAddressesFromInstallationSnapshot(shipRaw);
  }

  let installationAddressId = "";
  if (same && bilId) {
    installationAddressId = bilId;
  } else if (!same) {
    if (resolvedFromList) {
      installationAddressId = installationAddressDisplayFromSnapshot(resolvedFromList);
    } else if (shipRaw) {
      installationAddressId = installationAddressDisplayFromSnapshot(shipRaw);
    }
  }

  return {
    formik: {
      isSameAsBilling: same,
      billingAddressId: bilId ? bilId : "",
      installationAddressId,
    },
    useOfficeBilling,
    billingAddressObj: bilAddr,
    installationAddressObj: instAddr,
  };
}

/** Installation priority: 1 = High, 2 = Medium, 3 = Low. Coerces API string/number so react-select options match. */
export function normalizeInstallationPriority(p: unknown): 1 | 2 | 3 | null {
  if (p == null || p === "") return null;
  const n = Number(p);
  if (n === 1 || n === 2 || n === 3) return n as 1 | 2 | 3;
  return null;
}
